import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateCustomerInput, UpdateCustomerInput } from '@/lib/marina/validators'

export interface Customer {
  id: string
  firstName: string
  lastName: string
  email: string | null
  type: 'INDIVIDUAL' | 'COMPANY'
  category: 'TRANSIT' | 'OWNER' | 'RENTER' | 'MEMBER' | 'GUEST' | 'OTHER'
  phone: string | null
  mobile: string | null
  address: string | null
  city: string | null
  country: string | null
  postalCode: string | null
  website: string | null
  idType: string | null
  idNumber: string | null
  idExpiry: string | null
  dateOfBirth: string | null
  nationality: string | null
  billingName: string | null
  billingAddress: string | null
  billingCity: string | null
  billingCountry: string | null
  billingPostalCode: string | null
  taxId: string | null
  companyName: string | null
  companyRegNumber: string | null
  hasPortalAccess: boolean
  lastLoginAt: string | null
  checkedIn: boolean
  notes: string | null
  tags: string[]
  createdAt: string
  updatedAt: string
  _count?: { boats: number; bookings: number; invoices: number; contracts: number; quotes?: number }
  boats?: { id: string; name: string }[]
}

export interface CustomersListResponse {
  items: Customer[]
  total: number
  page: number
  pageSize: number
}

export interface CustomersListParams {
  q?: string
  category?: Customer['category']
  type?: Customer['type']
  page?: number
  pageSize?: number
  sort?: string
  order?: 'asc' | 'desc'
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

function toQuery(params: Record<string, unknown>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export function useCustomers(params: CustomersListParams = {}) {
  return useQuery({
    queryKey: ['customers', params],
    queryFn: () => apiFetch<CustomersListResponse>(`/api/customers${toQuery(params as Record<string, unknown>)}`),
    staleTime: 15_000,
  })
}

export function useCustomer(id: string | null | undefined) {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: () => apiFetch<{ customer: Customer }>(`/api/customers/${id}`),
    enabled: !!id,
  })
}

export function useCreateCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCustomerInput) =>
      apiFetch<{ customer: Customer }>('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  })
}

export function useUpdateCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomerInput }) =>
      apiFetch<{ customer: Customer }>(`/api/customers/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      qc.invalidateQueries({ queryKey: ['customer', vars.id] })
    },
  })
}

export function useDeleteCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean }>(`/api/customers/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  })
}
